import { atom, map } from 'nanostores';
import { PROVIDER_LIST } from '~/lib/provider-list';
import type { IProviderConfig } from '~/types/model';
import { toggleTheme, applyServerTheme } from './theme';
import { create } from 'zustand';
import { apiGetPreferences, apiUpdatePreferences } from '~/lib/api/user-settings';
import type { UserPreferences } from '@shared/types/user-settings';

export interface Shortcut {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
  ctrlOrMetaKey?: boolean;
  action: () => void;
  description?: string;
  isPreventDefault?: boolean;
}

export interface Shortcuts {
  toggleTheme: Shortcut;
  toggleTerminal: Shortcut;
}

export type ProviderSetting = Record<string, IProviderConfig>;

export const shortcutsStore = map<Shortcuts>({
  toggleTheme: {
    key: 'd',
    metaKey: true,
    altKey: true,
    shiftKey: true,
    action: () => toggleTheme(),
    description: 'Toggle theme',
    isPreventDefault: true,
  },
  toggleTerminal: {
    key: '`',
    ctrlOrMetaKey: true,
    action: () => {},
    description: 'Toggle terminal',
    isPreventDefault: true,
  },
});

const getDefaultProviderSettings = (): ProviderSetting => {
  const initialSettings: ProviderSetting = {};

  PROVIDER_LIST.forEach((provider) => {
    initialSettings[provider.name] = {
      ...provider,
      settings: {
        enabled: true,
      },
    };
  });

  return initialSettings;
};

export const providersStore = map<ProviderSetting>(getDefaultProviderSettings());

export function syncProvidersFromApi(providers: any[]): void {
  const current = providersStore.get();

  for (const provider of providers) {
    if (!current[provider.name]) {
      providersStore.setKey(provider.name, {
        ...provider,
        settings: { enabled: true },
      });
    }
  }
}

export const updateProviderSettings = (provider: string, settings: ProviderSetting) => {
  const currentSettings = providersStore.get();

  const updatedProvider = {
    ...currentSettings[provider],
    settings: {
      ...currentSettings[provider].settings,
      ...settings,
    },
  };

  providersStore.setKey(provider, updatedProvider);

  const allSettings = providersStore.get();
  const providerSettingsPatch: Record<string, { enabled: boolean }> = {};

  for (const [name, config] of Object.entries(allSettings)) {
    providerSettingsPatch[name] = { enabled: config.settings.enabled ?? true };
  }

  apiUpdatePreferences({ providerSettings: providerSettingsPatch }).catch((err) =>
    console.error('Failed to sync provider settings:', err),
  );
};

export const isDebugMode = atom(false);

const SETTING_DEFAULTS = {
  latestBranch: false,
  eventLogs: true,
  developerMode: false,
};

export const latestBranchStore = atom<boolean>(SETTING_DEFAULTS.latestBranch);
export const isEventLogsEnabled = atom<boolean>(SETTING_DEFAULTS.eventLogs);

export const updateLatestBranch = (enabled: boolean) => {
  latestBranchStore.set(enabled);
  apiUpdatePreferences({ isLatestBranch: enabled }).catch((err) => console.error('Failed to sync setting:', err));
};

export const updateEventLogs = (enabled: boolean) => {
  isEventLogsEnabled.set(enabled);
  apiUpdatePreferences({ isEventLogsEnabled: enabled }).catch((err) => console.error('Failed to sync setting:', err));
};

const LEGACY_STORAGE_KEYS = ['provider_settings', 'isLatestBranch', 'isEventLogsEnabled', 'isDeveloperMode'];

export async function initializePreferencesFromServer(): Promise<void> {
  try {
    let prefs = await apiGetPreferences();

    if (typeof window !== 'undefined' && Object.keys(prefs).length === 0) {
      const migrated = _collectLocalStoragePreferences();

      if (Object.keys(migrated).length > 0) {
        prefs = await apiUpdatePreferences(migrated);
        LEGACY_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
      }
    } else if (typeof window !== 'undefined') {
      LEGACY_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    }

    _applyPreferences(prefs);
  } catch (err) {
    console.error('Failed to initialize preferences from server:', err);
  }
}

function _collectLocalStoragePreferences(): Partial<UserPreferences> {
  const patch: Partial<UserPreferences> = {};

  try {
    const stored = (key: string) => localStorage.getItem(key);

    const theme = stored('dunea_theme');

    if (theme === 'dark' || theme === 'light') {
      patch.theme = theme;
    }

    const latestBranch = stored('isLatestBranch');

    if (latestBranch !== null) {
      patch.isLatestBranch = JSON.parse(latestBranch);
    }

    const eventLogs = stored('isEventLogsEnabled');

    if (eventLogs !== null) {
      patch.isEventLogsEnabled = JSON.parse(eventLogs);
    }

    const devMode = stored('isDeveloperMode');

    if (devMode !== null) {
      patch.isDeveloperMode = JSON.parse(devMode);
    }

    const providerSettings = stored('provider_settings');

    if (providerSettings !== null) {
      const parsed = JSON.parse(providerSettings);
      const simplified: Record<string, { enabled: boolean }> = {};

      for (const [name, config] of Object.entries(parsed)) {
        simplified[name] = { enabled: (config as any)?.settings?.enabled ?? true };
      }

      patch.providerSettings = simplified;
    }
  } catch (err) {
    console.error('Error collecting localStorage preferences for migration:', err);
  }

  return patch;
}

function _applyPreferences(prefs: UserPreferences): void {
  if (prefs.theme) {
    applyServerTheme(prefs.theme);
  }

  if (prefs.isLatestBranch !== undefined) {
    latestBranchStore.set(prefs.isLatestBranch);
  }

  if (prefs.isEventLogsEnabled !== undefined) {
    isEventLogsEnabled.set(prefs.isEventLogsEnabled);
  }

  if (prefs.providerSettings) {
    const currentSettings = providersStore.get();

    for (const [name, setting] of Object.entries(prefs.providerSettings)) {
      if (currentSettings[name]) {
        providersStore.setKey(name, {
          ...currentSettings[name],
          settings: { ...currentSettings[name].settings, enabled: setting.enabled },
        });
      }
    }
  }
}

interface SettingsStore {
  isOpen: boolean;
  selectedTab: string;
  openSettings: () => void;
  closeSettings: () => void;
  setSelectedTab: (tab: string) => void;
}

export const useSettingsStore = create<SettingsStore>((set) => ({
  isOpen: false,
  selectedTab: 'profile',

  openSettings: () => {
    set({
      isOpen: true,
      selectedTab: 'profile',
    });
  },

  closeSettings: () => {
    set({
      isOpen: false,
      selectedTab: 'profile',
    });
  },

  setSelectedTab: (tab: string) => {
    set({ selectedTab: tab });
  },
}));
